import ApiService from "@/core/services/api.service";
import { login, verifyUser } from "@/api/login";
import { updatePassword, updateEmail, resetToken } from "@/api/user";
import JwtService from "@/core/services/jwt.service";

// action types
export const VERIFY_AUTH = "verifyAuth";
export const LOGIN = "login";
export const LOGOUT = "logout";
export const REGISTER = "register";
export const UPDATE_PASSWORD = "updatePassword";
export const UPDATE_EMAIL = "updateEmail";
export const RESET_CUSTOM_TOKEN = "resetCustomToken";

// mutation types
export const PURGE_AUTH = "logOut";
// SET_AUTH = 建立会话（登录成功，写 localStorage）；SET_USER = 只刷新内存里的用户信息。
// 两者必须分开：main.js 的 router.beforeEach 每次路由切换都 dispatch VERIFY_AUTH，
// 而 /api/v1/user/verify/ 只是原样回显客户端发来的 token、并固定返回 expiration=86400
// （bees/api app/api_1_0/resources/auth.py:322），不签发新 token。若 VERIFY_AUTH 也走
// SET_AUTH，等于每翻一页就把本地过期时刻往后推 24h —— token 早已失效而前端永远认为有效，
// 用户进到控制台后才被 API 的 10009 弹窗踢出。
export const SET_AUTH = "setUser";
export const SET_USER = "setUserProfile";
export const SET_PASSWORD = "setPassword";
export const SET_EMAIL = "setEmail";
export const SET_CUSTOM_TOKEN = "setCustomToken";

export const SET_ERROR = "setError";

const state = {
  errors: null,
  user: {},
  balance: 0,
  // 只看 token 存在是不够的：过期的 token 字符串仍留在 localStorage，
  // 据此放行会让用户进到控制台后才被 API 的 10009 弹窗踢出（见 request.js 的响应拦截）。
  // isSessionValid 与 bees/www 的 isLoggedIn 同口径，两侧对「我登录了吗」给出一致答案。
  isAuthenticated: JwtService.isSessionValid(),
};

const getters = {
  currentUser(state) {
    return state.user;
  },
  isAuthenticated(state) {
    return state.isAuthenticated;
  },
  currentUserBalance(state) {
    return state.balance;
  },
};

const actions = {
  [LOGIN](context, credentials) {
    return new Promise((resolve, reject) => {
      login(credentials.phone, credentials.password)
        .then((data) => {
          context.commit(SET_AUTH, data);
          resolve(data);
        })
        .catch((error) => {
          context.commit(SET_ERROR, error);
          reject(error);
        });
    });
  },
  [LOGOUT](context) {
    context.commit(PURGE_AUTH);
  },
  [REGISTER](context, credentials) {
    return new Promise((resolve) => {
      ApiService.post("login", credentials)
        .then((data) => {
          context.commit(SET_AUTH, data);
          resolve(data);
        })
        .catch((response) => {
          context.commit(SET_ERROR, response.data.errors);
        });
    });
  },
  [VERIFY_AUTH](context) {
    // 用 isSessionValid 而非 getToken：过期会话不必再往后端跑一趟 verify，
    // 直接 PURGE_AUTH 走踢出流程。
    if (JwtService.isSessionValid()) {
      return new Promise((resolve, reject) => {
        verifyUser()
          .then((data) => {
            // SET_USER 而非 SET_AUTH：verify 不签发新 token，不该重写会话有效期。
            context.commit(SET_USER, data);
            resolve(data);
          })
          .catch((response) => {
            context.commit(SET_ERROR, response.data.errors);
            reject(response);
          });
      });
    } else {
      context.commit(PURGE_AUTH);
    }
  },
  [UPDATE_PASSWORD](context, payload) {
    return new Promise((resolve) => {
      updatePassword(payload).then((data) => {
        resolve(data);
      });
    });
  },
  [UPDATE_EMAIL](context, payload) {
    updateEmail(payload).then((data) => {
      context.commit(SET_EMAIL, payload.email);
      return data;
    });
  },
  [RESET_CUSTOM_TOKEN](context) {
    resetToken().then((data) => {
      context.commit(SET_CUSTOM_TOKEN, data.result.token);
      return data;
    });
  },
};

const mutations = {
  [SET_ERROR](state, error) {
    state.errors = error;
  },
  [SET_AUTH](state, user) {
    state.isAuthenticated = true;
    state.user = user.result.user;
    state.balance = user.result.user.balance / 100;
    state.errors = {};
    // 先落库再贴 UI 字段：photo 是本仓的展示需要，不属于跨应用契约，
    // 不能进 user_info（否则 bees/www 会读到一个带 console 专属路径的 user 对象）。
    JwtService.saveSession(
      user.result.token,
      user.result.user,
      user.result.expiration
    );
    state.user.photo = "/oms/media/users/default.jpg";
  },
  // 只更新内存中的用户信息，不碰 localStorage —— 会话有效期只由登录时刻决定。
  [SET_USER](state, user) {
    state.isAuthenticated = true;
    state.user = user.result.user;
    state.balance = user.result.user.balance / 100;
    state.errors = {};
    state.user.photo = "/oms/media/users/default.jpg";
  },
  [SET_EMAIL](state, email) {
    state.user.email = email;
  },
  [SET_CUSTOM_TOKEN](state, token) {
    state.user.token = token;
  },
  [PURGE_AUTH](state) {
    state.isAuthenticated = false;
    state.user = {};
    state.balance = 0;
    state.errors = {};
    // 清全部三个 key。只清 token 会把 user_info（含用户资料）和 token_expiration
    // 留在磁盘上，且让 bees/www 侧读到一份没有 token 的残缺会话。
    JwtService.destroySession();
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
